import { IcAltitude } from '@assets/icons/24x24';
import { For, HStack, Text, VStack } from '@chakra-ui/react';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectAltitudeUnit } from '@redux/settings/settings.selectors';
import {
  selectAllPerimeterCameras,
  selectAutomatonSectionAssociatedLadCameraName,
  selectAutomatonSegmentStatusByKey,
} from '@redux/situation/monitoring.selectors';
import { getSegmentStatus } from '@utils/map/automatonSection.utils';
import { getSensorOrSubSensorAltitude } from '@utils/map/sensor.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  AutomatonSectionData,
  AutomatonSectionStatusEnumWithMaintenance,
  AutomatonSegment,
} from '@/types/sensor/sensors';

import CoordinatesInformation from '../../common/CoordinatesInformation';
import IconValue from '../IconValue';
import InfoValue from '../InfoValue';

type Props = {
  automatonSectionData: AutomatonSectionData;
  upperSegment: AutomatonSegment | null;
  lowerSegment: AutomatonSegment | null;
  lowerStatus: AutomatonSectionStatusEnumWithMaintenance | null;
};
export default function AutomatonSectionInformation({
  automatonSectionData,
  upperSegment,
  lowerSegment,
  lowerStatus,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  const { automaton, cabinet, section, perimeterCameraUniqueKeys } = automatonSectionData;
  const upperStatus: AutomatonSectionStatusEnumWithMaintenance | null = useSelectorWithReplayMode(
    selectAutomatonSegmentStatusByKey,
    {
      automaton: automaton.uniqueKey,
      cabinet: cabinet.uniqueKey,
      segment: upperSegment ? upperSegment.uniqueKey : '',
    },
  );
  const upperStatusValue = upperSegment && getSegmentStatus(upperSegment, upperStatus, automaton, cabinet);
  const lowerStatusValue = lowerSegment && getSegmentStatus(lowerSegment, lowerStatus, automaton, cabinet);

  const perimeterCameras = useSelectorWithReplayMode(selectAllPerimeterCameras);

  const ladCameraName = useSelectorWithReplayMode(selectAutomatonSectionAssociatedLadCameraName, section);

  return (
    <VStack gap={0.25} width="100%">
      <PopupContainer>
        <VStack gap={3} width="100%">
          <HStack gap={3} width="100%">
            {upperSegment && (
              <InfoValue
                label={formatMessage({ id: 'automatonSection.tab.information.upperSegment' })}
                value={formatMessage({
                  id: `automatonSection.segment.status.${upperStatusValue}`,
                })}
              />
            )}
            {lowerSegment && (
              <InfoValue
                label={formatMessage({ id: 'automatonSection.tab.information.lowerSegment' })}
                value={formatMessage({
                  id: `automatonSection.segment.status.${lowerStatusValue}`,
                })}
              />
            )}
          </HStack>
        </VStack>
      </PopupContainer>
      <PopupContainer>
        <VStack width="100%" gap={3}>
          <HStack width="100%" gap={3}>
            <InfoValue
              label={formatMessage({ id: 'automatonSection.tab.information.automaton' })}
              value={
                <Text
                  paddingY={0.25}
                  paddingX={0.5}
                  backgroundColor="neutral.black"
                  fontWeight="medium"
                  textStyle="screenStd"
                >
                  {automaton.name}
                </Text>
              }
            />
            <InfoValue
              label={formatMessage({ id: 'automatonSection.tab.information.cabinet' })}
              value={
                <Text
                  paddingY={0.25}
                  paddingX={0.5}
                  backgroundColor="neutral.black"
                  fontWeight="medium"
                  textStyle="screenStd"
                >
                  {cabinet.name}
                </Text>
              }
            />
          </HStack>
          <InfoValue
            label={formatMessage({ id: 'automatonSection.tab.information.perimeterCamera' })}
            value={
              <HStack width="100%" gap={1} flexWrap="wrap">
                <For
                  each={perimeterCameraUniqueKeys}
                  fallback={
                    <Text textStyle="screenStd" fontWeight="medium">
                      <FormattedMessage id="automatonSection.tab.information.noPerimeterCamera" />
                    </Text>
                  }
                >
                  {(uniqueKey) => {
                    const label = perimeterCameras.find((camera) => camera.uniqueKey === uniqueKey)?.name ?? uniqueKey;
                    return (
                      <Text
                        paddingY={0.25}
                        paddingX={0.5}
                        backgroundColor="neutral.black"
                        fontWeight="medium"
                        textStyle="screenStd"
                      >
                        {label}
                      </Text>
                    );
                  }}
                </For>
              </HStack>
            }
          />
          <InfoValue
            label={formatMessage({ id: 'automatonSection.tab.information.ladCamera' })}
            value={
              ladCameraName ? (
                <Text
                  paddingY={0.25}
                  paddingX={0.5}
                  backgroundColor="neutral.black"
                  fontWeight="medium"
                  textStyle="screenStd"
                >
                  {ladCameraName}
                </Text>
              ) : (
                <Text textStyle="screenStd" fontWeight="medium">
                  <FormattedMessage id="automatonSection.tab.information.noLadCamera" />
                </Text>
              )
            }
          />
        </VStack>
      </PopupContainer>
      <PopupContainer>
        <VStack gap="standardXS" textStyle="screenStd" width="100%" alignItems="start">
          <Text color="neutral.300">
            <FormattedMessage id="automatonSection.tab.information.fixedPosition" />
          </Text>
          <IconValue
            icon={IcAltitude}
            value={formatUnit(
              altitudeUnit,
              convertDistanceUnitFromMeter(
                altitudeUnit,
                getSensorOrSubSensorAltitude(section.upperSegment ?? section.lowerSegment),
              ),
            )}
          />
        </VStack>
      </PopupContainer>
      <CoordinatesInformation coordinates={(section.upperSegment?.position ?? section.lowerSegment?.position)!} />
    </VStack>
  );
}
